import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = ['table', 'row', 'selectAll', 'bulkBar', 'bulkCount', 'bulkField', 'bulkValue']

  connect() {
    this.element.dataset.crmGrid = 'ready'
    this.searchUrl = this.data.get('searchUrl')
    this.bulkUrl = this.data.get('bulkUrl')
    this.bulkDestroyUrl = this.data.get('bulkDestroyUrl')
    this.editing = null // { td, row, cellIndex, originalHTML }
    this.onClick = this.onClick.bind(this)
    this.element.addEventListener('click', this.onClick)
  }

  disconnect() {
    this.element.removeEventListener('click', this.onClick)
  }

  onClick(e) {
    const td = e.target.closest('.crm-cell-edit')
    if (!td || !this.element.contains(td)) return
    if (this.editing && this.editing.td === td) return
    // A click elsewhere while editing: commit-by-blur already handled it.
    if (td.querySelector('.crm-editor')) return
    e.preventDefault()
    this.startEdit(td)
  }

  startEdit(td) {
    if (this.editing) this.cancel()
    const row = td.closest('tr')
    const type = td.dataset.type
    const field = td.dataset.field
    const value = td.dataset.value || ''
    this.editing = { td, row, cellIndex: td.cellIndex, originalHTML: td.innerHTML, field }

    if (type === 'select') this.buildSelect(td, value)
    else if (type === 'search') this.buildSearch(td, value)
    else this.buildInput(td, type, value)
  }

  buildInput(td, type, value) {
    const input = document.createElement('input')
    input.type = type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'
    input.value = value
    input.className = 'crm-editor w-full bg-white border border-blue-400 rounded px-2 py-1 text-[13px] outline-none ring-2 ring-blue-100'
    td.innerHTML = ''
    td.appendChild(input)
    input.focus()
    if (input.select) input.select()
    input.addEventListener('keydown', (e) => this.onEditorKey(e, () => input.value))
    input.addEventListener('blur', () => this.commit(input.value, null))
  }

  buildSelect(td, value) {
    const options = JSON.parse(td.dataset.options || '[]')
    const sel = document.createElement('select')
    sel.className = 'crm-editor w-full bg-white border border-blue-400 rounded px-1.5 py-1 text-[13px] outline-none ring-2 ring-blue-100'
    options.forEach(([val, label]) => {
      const o = document.createElement('option')
      o.value = val
      o.textContent = label
      if (String(val) === String(value)) o.selected = true
      sel.appendChild(o)
    })
    td.innerHTML = ''
    td.appendChild(sel)
    sel.focus()
    sel.addEventListener('change', () => this.commit(sel.value, 'down'))
    sel.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); this.cancel() } })
    sel.addEventListener('blur', () => { if (this.editing && this.editing.td === td) this.cancel() })
  }

  buildSearch(td, value) {
    const kind = td.dataset.kind
    const clientId = td.dataset.clientId || ''
    const wrap = document.createElement('div')
    wrap.className = 'crm-editor relative'
    const input = document.createElement('input')
    input.type = 'text'
    input.placeholder = td.dataset.display || ''
    input.className = 'w-full bg-white border border-blue-400 rounded px-2 py-1 text-[13px] outline-none ring-2 ring-blue-100'
    const menu = document.createElement('div')
    menu.className = 'absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto hidden'
    wrap.appendChild(input)
    wrap.appendChild(menu)
    td.innerHTML = ''
    td.appendChild(wrap)
    input.focus()

    let seq = 0
    const search = () => {
      const q = input.value.trim()
      const params = new URLSearchParams({ kind, q })
      if (kind === 'contact') params.set('client_id', clientId)
      const mySeq = ++seq
      fetch(`${this.searchUrl}?${params.toString()}`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((rows) => {
          if (mySeq !== seq) return
          menu.innerHTML = ''
          if (!rows.length) { menu.classList.add('hidden'); return }
          rows.forEach((row) => {
            const item = document.createElement('button')
            item.type = 'button'
            item.className = 'block w-full text-left px-3 py-1.5 text-[13px] text-slate-700 hover:bg-blue-50'
            item.textContent = row.name
            item.addEventListener('mousedown', (e) => { e.preventDefault(); this.commit(String(row.id), 'right') })
            menu.appendChild(item)
          })
          menu.classList.remove('hidden')
        })
        .catch(() => menu.classList.add('hidden'))
    }
    let t
    input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(search, 200) })
    input.addEventListener('focus', search)
    input.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); this.cancel() } })
    input.addEventListener('blur', () => setTimeout(() => { if (this.editing && this.editing.td === td) this.cancel() }, 150))
  }

  onEditorKey(e, getValue) {
    if (e.key === 'Enter') { e.preventDefault(); this.commit(getValue(), 'down') }
    else if (e.key === 'Tab') { e.preventDefault(); this.commit(getValue(), e.shiftKey ? 'left' : 'right') }
    else if (e.key === 'Escape') { e.preventDefault(); this.cancel() }
  }

  commit(value, move) {
    if (!this.editing) return
    const { td, row, field, originalHTML, cellIndex } = this.editing
    const previous = td.dataset.value || ''
    if (String(value) === String(previous)) { td.innerHTML = originalHTML; this.editing = null; if (move) this.moveFrom(row, cellIndex, move); return }
    this.editing = null
    td.innerHTML = '<span class="text-slate-300 text-[12px]"><i class="fas fa-circle-notch fa-spin"></i></span>'
    const id = row.dataset.id
    fetch(row.dataset.updateUrl, {
      method: 'PATCH',
      headers: this.headers(),
      body: JSON.stringify({ field, value })
    })
      .then((r) => { if (!r.ok) throw new Error('inline update failed'); return r.text() })
      .then((html) => {
        const fresh = this.element.querySelector(`tr[data-id="${id}"]`)
        if (fresh) {
          fresh.innerHTML = html
          this.updateBulk()
          const cols = this.application.getControllerForElementAndIdentifier(this.element, 'crm-columns')
          if (cols) { cols.apply(); cols.applyRowOrder(fresh) }
          if (move) this.moveFrom(fresh, cellIndex, move)
        }
      })
      .catch(() => { td.innerHTML = originalHTML })
  }

  cancel() {
    if (!this.editing) return
    const { td, originalHTML } = this.editing
    td.innerHTML = originalHTML
    this.editing = null
  }

  moveFrom(row, cellIndex, dir) {
    let target = null
    if (dir === 'down') {
      const next = row.nextElementSibling
      if (next && next.cells && next.cells[cellIndex]) target = next.cells[cellIndex]
    } else if (dir === 'right' || dir === 'left') {
      const cells = Array.from(row.cells)
      const editable = cells.filter((c) => c.classList.contains('crm-cell-edit'))
      const idx = editable.indexOf(row.cells[cellIndex])
      // cellIndex may not point at an editable cell after re-render; fall back to nearest.
      let pos = idx
      if (pos === -1) pos = editable.findIndex((c) => c.cellIndex >= cellIndex)
      const nextEditable = editable[dir === 'right' ? pos + 1 : pos - 1]
      target = nextEditable || null
    }
    if (target && target.classList.contains('crm-cell-edit')) {
      // Defer so the just-committed row is fully in the DOM.
      setTimeout(() => this.startEdit(target), 0)
    }
  }

  allChecks() {
    return Array.from(this.element.querySelectorAll('.crm-row-check'))
  }

  selectedIds() {
    return this.allChecks().filter((c) => c.checked).map((c) => c.dataset.id)
  }

  toggleAll(e) {
    const on = e.target.checked
    this.allChecks().forEach((c) => { c.checked = on })
    this.updateBulk()
  }

  onRowCheck() {
    this.updateBulk()
  }

  updateBulk() {
    const ids = this.selectedIds()
    const n = ids.length
    if (this.hasBulkCountTarget) this.bulkCountTarget.textContent = n
    if (this.hasBulkBarTarget) this.bulkBarTarget.classList.toggle('hidden', n === 0)
    if (this.hasSelectAllTarget) {
      const total = this.allChecks().length
      this.selectAllTarget.checked = n > 0 && n === total
      this.selectAllTarget.indeterminate = n > 0 && n < total
    }
  }

  onBulkField() {
    const field = this.bulkFieldTarget.value
    this.bulkValueTarget.querySelectorAll('[data-bulk-control]').forEach((el) => {
      el.classList.toggle('hidden', el.dataset.bulkControl !== field)
    })
  }

  applyBulk() {
    const ids = this.selectedIds()
    const field = this.bulkFieldTarget.value
    if (!ids.length || !field) return
    const control = this.bulkValueTarget.querySelector(`[data-bulk-control="${field}"]`)
    const value = control ? control.value : ''
    fetch(this.bulkUrl, {
      method: 'PATCH',
      headers: this.headers(),
      body: JSON.stringify({ ids, field, value })
    })
      .then((r) => { if (!r.ok) throw new Error('bulk update failed'); return r.json() })
      .then(() => window.location.reload())
      .catch(() => window.location.reload())
  }

  bulkDelete() {
    const ids = this.selectedIds()
    if (!ids.length) return
    if (!window.confirm((this.data.get('deleteConfirm') || 'Supprimer ?').replace('%{count}', ids.length))) return
    fetch(this.bulkDestroyUrl, {
      method: 'DELETE',
      headers: this.headers(),
      body: JSON.stringify({ ids })
    })
      .then((r) => { if (!r.ok) throw new Error('bulk delete failed'); return r.json() })
      .then(() => window.location.reload())
      .catch(() => window.location.reload())
  }

  bulkArchive() { this.bulkArchiveToggle(true) }

  bulkUnarchive() { this.bulkArchiveToggle(false) }

  bulkArchiveToggle(archived) {
    const ids = this.selectedIds()
    if (!ids.length) return
    fetch(this.data.get('bulkArchiveUrl'), {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ ids, archived })
    })
      .then((r) => { if (!r.ok) throw new Error('bulk archive failed'); return r.json() })
      .then(() => window.location.reload())
      .catch(() => window.location.reload())
  }

  clearSelection() {
    this.allChecks().forEach((c) => { c.checked = false })
    this.updateBulk()
  }

  headers() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token ? token.content : '',
      Accept: 'text/html'
    }
  }
}
